import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  TrackByFunction,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { SafeHtml } from '@angular/platform-browser';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { ActionListData, ChatMsgVM } from '../../../models/message.types';
import {
  AiChatActionHandlerService,
  MarkdownRendererService,
} from '../../../services';
import { AiChatActionCardComponent } from '../ai-chat-action-card/ai-chat-action-card.component';
import { AiChatActionListComponent } from '../ai-chat-action-list/ai-chat-action-list.component';
import { AiChatFeedbackComponent } from '../ai-chat-feedback/ai-chat-feedback.component';
import { AiChatThinkingBlockComponent } from '../ai-chat-thinking-block/ai-chat-thinking-block.component';
import { TypingLottieComponent } from '../typing-lottie/typing-lottie.component';

@Component({
  selector: 'qt-ai-chat-enhanced-message-bubble',
  standalone: true,
  imports: [
    MatIconModule,
    TranslationCoreModule,
    AiChatActionCardComponent,
    AiChatActionListComponent,
    AiChatFeedbackComponent,
    AiChatThinkingBlockComponent,
    TypingLottieComponent,
  ],
  template: `
    <div
      class="qt-msg"
      [class.qt-msg--ai]="isBotMessage()"
      [class.qt-msg--user]="isUserMessage()">
      @if (isBotMessage() || hasMessageContent()) {
        <div
          class="qt-msg__bubble"
          [class.qt-msg__bubble--typing]="isTypingSignal()">
          <!-- Message content with custom components -->
          @if (hasMessageContent()) {
            <div
              class="qt-msg__content"
              (click)="onMessageContentClick($event)">
              @for (part of messageParts(); track trackByPart($index, part)) {
                <div class="qt-msg__part">
                  <!-- Action List Component -->
                  @if (
                    part.type === 'action_list' && isActionListData(part.data)
                  ) {
                    <qt-ai-chat-action-list [data]="part.data">
                    </qt-ai-chat-action-list>
                  }

                  <!-- Thinking Block Component -->
                  @if (part.type === 'thinking') {
                    <qt-ai-chat-thinking-block [content]="part.content">
                    </qt-ai-chat-thinking-block>
                  }

                  <!-- Action Card (e.g. send-email) -->
                  @if (part.type === 'action_card' && part.data) {
                    <qt-ai-chat-action-card
                      [payload]="part.data"
                      [userPromptText]="previousMessageText()">
                    </qt-ai-chat-action-card>
                  }

                  <!-- Regular HTML content -->
                  @if (part.type === 'html') {
                    <div [innerHTML]="part.htmlContent"></div>
                  }
                </div>
              }
            </div>
          }

          @if (isTypingSignal()) {
            <div class="qt-typing-row">
              @if (typingLottieSrc()) {
                <qt-typing-lottie
                  [src]="typingLottieSrc()!"
                  [size]="typingLottieSize()" />
              } @else {
                <span class="qt-typing-dots">
                  @for (
                    dot of typingDots();
                    track trackByDot($index, dot);
                    let i = $index
                  ) {
                    <span
                      class="qt-typing-dot"
                      [style.animationDelay.ms]="i * 160">
                    </span>
                  }
                </span>
              }
              @if (toolStatusLabel()) {
                <div class="qt-tool-event-label">{{ toolStatusLabel() }}</div>
              }
            </div>
          }
        </div>
      }

      @if (showAttachmentIcon()) {
        <div class="qt-msg__attachments-row">
          @if (attachmentNames().length > 0) {
            @for (name of attachmentNames(); track $index) {
              <span class="qt-msg__attachment-pill">
                <mat-icon class="qt-msg__attachment-pill-icon"
                  >attach_file</mat-icon
                >
                <span class="qt-msg__attachment-pill-name" [title]="name">{{
                  name
                }}</span>
              </span>
            }
          } @else {
            <span class="qt-msg__attachment-pill">
              <mat-icon class="qt-msg__attachment-pill-icon"
                >attach_file</mat-icon
              >
              <span class="qt-msg__attachment-pill-name"
                >{{ attachmentCount() }} file{{
                  attachmentCount() > 1 ? 's' : ''
                }}</span
              >
            </span>
          }
        </div>
      }

      @if (hasToolsUsed() && !isTypingSignal()) {
        <div class="qt-tools-used">
          <button
            type="button"
            class="qt-tools-used__toggle"
            (click)="toggleToolsExpanded()">
            <mat-icon class="qt-tools-used__icon">
              {{ toolsExpanded() ? 'expand_less' : 'expand_more' }}
            </mat-icon>
            <span class="qt-tools-used__summary">{{ toolsUsedSummary() }}</span>
          </button>
          @if (toolsExpanded()) {
            <ul class="qt-tools-used__list">
              @for (tool of toolsUsed(); track tool.toolName) {
                <li
                  class="qt-tools-used__item"
                  [class.qt-tools-used__item--failed]="tool.failed">
                  <span class="qt-tools-used__label">{{ tool.label }}</span>
                  <span class="qt-tools-used__duration">{{
                    formatDuration(tool.durationMs)
                  }}</span>
                </li>
              }
            </ul>
          }
        </div>
      }

      @if (isErrored()) {
        <div class="qt-msg__interrupted">
          <mat-icon class="qt-msg__interrupted-icon">error_outline</mat-icon>
          <span class="qt-msg__interrupted-text">{{
            interruptedTextKey() | translate
          }}</span>
          <button type="button" class="qt-msg__retry" (click)="retry.emit()">
            <mat-icon class="qt-msg__retry-icon">refresh</mat-icon>
            <span>{{ 'GNR_ALLO_RETRY' | translate }}</span>
          </button>
        </div>
      }

      @if (showFeedback()) {
        <qt-ai-chat-feedback
          [chatId]="feedbackChatId()"
          [ts]="feedbackTs()"
          [messageText]="feedbackMessageText()">
        </qt-ai-chat-feedback>
      }
    </div>
  `,
  styleUrls: ['./ai-chat-enhanced-message-bubble.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatEnhancedMessageBubbleComponent {
  // Signal-based inputs
  readonly message = input<ChatMsgVM>();
  readonly previousMessageText = input<string>('');
  readonly isTyping = input(false);
  readonly typingDots = input<readonly number[]>([0, 1, 2]);
  readonly typingLottieSrc = input<string | undefined>(undefined);
  readonly typingLottieSize = input(48);
  readonly toolStatusLabel = input<string | null>(null);

  /** SD-2450: emitted when the user clicks Retry on an interrupted bubble. */
  readonly retry = output<void>();

  // Signals for reactive state management
  private readonly messageSignal = signal<ChatMsgVM | undefined>(undefined);
  readonly isTypingSignal = signal(false);

  // Computed signals
  readonly messageParts = computed(() => {
    const message = this.messageSignal();
    if (!message?.text) return [];

    return this.processMessageParts(message.text);
  });

  // Helper method to check if data is ActionListData
  isActionListData(data: unknown): data is ActionListData {
    return (
      typeof data === 'object' &&
      data !== null &&
      'type' in data &&
      (data as ActionListData).type === 'action_list' &&
      'itemList' in data &&
      Array.isArray((data as ActionListData).itemList)
    );
  }

  readonly isBotMessage = computed(
    () => this.isTypingSignal() || this.messageSignal()?.author === 'bot'
  );

  readonly isUserMessage = computed(
    () => !this.isTypingSignal() && this.messageSignal()?.author === 'user'
  );

  readonly hasMessageContent = computed(
    () => !this.isTypingSignal() && !!this.messageSignal()?.text
  );

  readonly showAttachmentIcon = computed(
    () =>
      !this.isTypingSignal() &&
      this.messageSignal()?.author === 'user' &&
      (this.messageSignal()?.attachmentCount ?? 0) > 0
  );

  readonly attachmentNames = computed(
    () => this.messageSignal()?.attachmentNames ?? []
  );

  readonly attachmentCount = computed(
    () => this.messageSignal()?.attachmentCount ?? 0
  );

  /** SD-2450: the turn was interrupted/failed mid-stream. */
  readonly isErrored = computed(
    () => !this.isTypingSignal() && !!this.messageSignal()?.errored
  );

  /** Copy key for the interrupted notice — varies by failure class. */
  readonly interruptedTextKey = computed(() =>
    this.messageSignal()?.errorCode === 'failed'
      ? 'GNR_ALLO_STREAM_FAILED'
      : 'GNR_ALLO_STREAM_INTERRUPTED'
  );

  readonly showFeedback = computed(
    () =>
      !this.isTypingSignal() &&
      !this.messageSignal()?.streaming &&
      !this.messageSignal()?.errored &&
      this.messageSignal()?.author === 'bot' &&
      !!this.messageSignal()?.text &&
      !!this.messageSignal()?.chatId &&
      this.messageSignal()?.ts != null
  );

  readonly feedbackChatId = computed(() => this.messageSignal()?.chatId ?? '');
  readonly feedbackTs = computed(() => this.messageSignal()?.ts ?? 0);
  readonly feedbackMessageText = computed(
    () => this.messageSignal()?.text ?? ''
  );

  readonly toolsUsed = computed(() => this.messageSignal()?.toolsUsed ?? []);
  readonly hasToolsUsed = computed(() => this.toolsUsed().length > 0);
  readonly toolsUsedSummary = computed(() => {
    const count = this.toolsUsed().length;
    return count === 1 ? 'Allo used 1 tool' : `Allo used ${count} tools`;
  });
  readonly toolsExpanded = signal(false);

  // Signal-based injection
  private readonly markdownRenderer = inject(MarkdownRendererService);
  private readonly actionHandler = inject(AiChatActionHandlerService);

  constructor() {
    // Effect to sync input properties with signals
    effect(() => {
      this.messageSignal.set(this.message());
      this.isTypingSignal.set(this.isTyping());
    });
  }

  toggleToolsExpanded(): void {
    this.toolsExpanded.update(v => !v);
  }

  formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  }

  trackByDot: TrackByFunction<number> = (index: number) => index;
  trackByPart: TrackByFunction<{
    type: 'html' | 'action_list' | 'thinking' | 'action_card';
    htmlContent?: SafeHtml;
    content?: string;
    data?: ActionListData | { content: string } | unknown;
  }> = (index: number, part) => `${part.type}-${index}`;

  onMessageContentClick(event: MouseEvent): void {
    this.actionHandler.onMessageContentClick(event);
  }

  private processMessageParts(text: string): Array<{
    type: 'html' | 'action_list' | 'thinking' | 'action_card';
    htmlContent?: SafeHtml;
    content?: string;
    data?: ActionListData | { content: string } | unknown;
  }> {
    if (!text) return [];

    const customComponents =
      this.markdownRenderer.extractCustomComponents(text);

    if (customComponents.length === 0) {
      // No custom components, render as regular HTML
      return [
        {
          type: 'html',
          htmlContent: this.markdownRenderer.renderToHtml(text),
        },
      ];
    }

    // Process text with custom components
    let processedText = text;
    const parts: Array<{
      type: 'html' | 'action_list' | 'thinking' | 'action_card';
      htmlContent?: SafeHtml;
      content?: string;
      data?: ActionListData | { content: string } | unknown;
    }> = [];

    // Replace custom components with placeholders and track their positions
    const componentMap = new Map<string, unknown>();
    customComponents.forEach((component, index) => {
      const placeholder = `__COMPONENT_${index}__`;
      componentMap.set(placeholder, component);
      processedText = processedText.replace(
        component.originalMatch,
        placeholder
      );
    });

    // Split by placeholders and process each part
    const textParts = processedText.split(/(__COMPONENT_\d+__)/);

    textParts.forEach(part => {
      if (part.startsWith('__COMPONENT_') && part.endsWith('__')) {
        const component = componentMap.get(part) as {
          type: string;
          data: ActionListData | { content: string } | unknown;
        };
        if (component) {
          if (component.type === 'action_list') {
            parts.push({
              type: 'action_list',
              data: component.data as ActionListData,
            });
          } else if (component.type === 'thinking') {
            parts.push({
              type: 'thinking',
              content: (component.data as { content: string }).content,
            });
          } else if (component.type === 'action_card') {
            parts.push({
              type: 'action_card',
              data: component.data,
            });
          }
        }
      } else if (part.trim()) {
        parts.push({
          type: 'html',
          htmlContent: this.markdownRenderer.renderToHtml(part),
        });
      }
    });

    // When the backend sends an action_card, show only the card(s) and hide the intro/summary text
    const hasActionCard = parts.some(p => p.type === 'action_card');
    if (hasActionCard) {
      return parts.filter(p => p.type !== 'html');
    }

    return parts;
  }
}
